import styled from 'styled-components';

import { S3_BASE_URL } from 'constants/static';
import { toAmPm } from 'utils/toAmPm';
import { usDateWithDayOfWeekFormat } from 'utils/usDateFormat';
import { useTranslation } from 'i18n';

const BOOKING_STATUS_KEY: Record<string, string> = {
  COMPLETED: 'booking.status.completed',
  READY: 'booking.status.ready',
  FAILED: 'booking.status.failed',
  CANCELED: 'booking.status.canceled',
  CANCELING: 'booking.status.canceling',
  CONFIRMED: 'booking.status.confirmed',
  UPCOMING: 'booking.status.upcoming',
};

interface BookingItem {
  bookingSeqNo: number;
  bookingStatusCd: 'COMPLETED' | 'READY' | 'FAILED' | 'CANCELED' | string; // 예약 상태
  bookingDate: string; // 예약 날짜
  bookingStartedAt: string; // 예약 시작 시간
  bookingEndedAt: string; // 예약 종료 시간
  shopRoomName: string; // 베이 정보
  bookingId: number;
}

interface BookingListCardProps {
  bookings: BookingItem[];
  onBookingClick?: (bookingId: number) => void;
  showSingleCard?: boolean; // 단일 카드 표시 여부
}

const CardListContainer = styled.div<{ $showSingleCard?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BookingCard = styled.div`
  border-radius: 4px;
  padding: 20px;
  background-color: #ffffff;
  align-items: flex-start;
  border: 1px solid #eaebef;
  cursor: pointer;
  color: #0f0f0f;
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

export const StatusIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StatusText = styled.span<{ $status: string }>`
  font-size: 16px;
  font-weight: 700;
  line-height: 150%;
  color: #0f0f0f;
`;

const BookingInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DateTime = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0;
  color: #0f0f0f;
`;

const BayInfo = styled.div`
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  color: #006300;
  letter-spacing: 0;
`;

export const STATUS_ICON_SRC: Record<BookingItem['bookingStatusCd'], string> = {
  COMPLETED: `${S3_BASE_URL}/booking_status_complete.svg`,
  UPCOMING: `${S3_BASE_URL}/booking_status_upcoming.svg`,
  FAILED: `${S3_BASE_URL}/booking_status_booked.svg`,
  CANCELED: `${S3_BASE_URL}/booking_status_canceled.svg`,
  CANCELING: `${S3_BASE_URL}/booking_status_canceled.svg`,
};

export const MyBookingCardList = ({
  bookings,
  onBookingClick,
  showSingleCard = false,
}: BookingListCardProps) => {
  const { t } = useTranslation();
  const handleBookingClick = (bookingId: number) => {
    if (onBookingClick) {
      onBookingClick(bookingId);
    }
  };

  // UPCOMING 상태를 항상 상단에 표시하도록 정렬
  const sortedBookings = [...bookings].sort((a, b) => {
    // UPCOMING이 아닌 것보다 UPCOMING을 우선
    if (a.bookingStatusCd === 'UPCOMING' && b.bookingStatusCd !== 'UPCOMING') {
      return -1;
    }
    if (a.bookingStatusCd !== 'UPCOMING' && b.bookingStatusCd === 'UPCOMING') {
      return 1;
    }
    // 같은 상태면 원래 순서 유지
    return 0;
  });

  // 단일 카드 모드일 때는 첫 번째 카드만 표시
  const displayBookings = showSingleCard ? sortedBookings.slice(0, 1) : sortedBookings;

  const transformBookingStatus = (status: string) => {
    const key = BOOKING_STATUS_KEY[status];
    return key ? t(key) : status;
  };

  return (
    <CardListContainer $showSingleCard={showSingleCard}>
      {displayBookings.map((booking: BookingItem) => (
        <BookingCard key={booking.bookingId} onClick={() => handleBookingClick(booking.bookingId)}>
          <StatusContainer>
            <StatusIcon>
              <img
                src={STATUS_ICON_SRC[booking.bookingStatusCd ?? 'FAILED']}
                alt={booking.bookingStatusCd}
                width={24}
                height={24}
              />
            </StatusIcon>
            <StatusText $status={booking.bookingStatusCd}>
              {transformBookingStatus(booking.bookingStatusCd)}
            </StatusText>
          </StatusContainer>

          <BookingInfo>
            <DateTime>
              {usDateWithDayOfWeekFormat(booking?.bookingDate)} · {toAmPm(booking.bookingStartedAt)}{' '}
              - {toAmPm(booking.bookingEndedAt)}
            </DateTime>
            <BayInfo>{booking.shopRoomName}</BayInfo>
          </BookingInfo>
        </BookingCard>
      ))}
    </CardListContainer>
  );
};
